import React from "react";
import { SingleValue } from "react-select";

import { KintoneFormFields } from "@graffer-inc/cloud-gov-core";
import {
  ReactSelect,
  ReactSelectOption,
} from "@graffer-inc/console-design-kit";
import { UpdateKintoneExportSettingInput } from "@graffer-inc/smart-apply";

import { kintoneFieldToReadableString, useKintoneFieldSelect } from "./hook";

export type KintoneFieldSelectProps = {
  value: UpdateKintoneExportSettingInput["elementFieldMappings"][number]["kintoneFieldCode"];
  onChange: (
    kintoneFieldCode: UpdateKintoneExportSettingInput["elementFieldMappings"][number]["kintoneFieldCode"],
  ) => void;
  kintoneFieldsMap: KintoneFormFields;
  label: string;
  description?: string;
  errorMessages?: string;
  shouldDisplayError?: boolean;
};

export const KintoneFieldSelect: React.FC<KintoneFieldSelectProps> = ({
  value,
  onChange,
  kintoneFieldsMap,
  label,
  description,
  errorMessages,
  shouldDisplayError,
}) => {
  const [selected, setSelected] = React.useState<
    { value: string; label: string } | undefined
  >(() => {
    if (!value) {
      return undefined;
    }
    const field = kintoneFieldsMap[value];
    if (!field) {
      // 基本的にありえない想定
      return {
        value,
        label: `存在しないフィールド ${value} が指定されています`,
      };
    }
    return {
      value: field.code,
      label: kintoneFieldToReadableString(field),
    };
  });

  const { options, onInputChange, isLoading } = useKintoneFieldSelect({
    candidates: kintoneFieldsMap,
  });

  const helpText = React.useMemo(() => {
    if (!value) {
      return undefined;
    }
    const field = kintoneFieldsMap[value];
    if (!field) {
      return undefined;
    }
    if ("options" in field) {
      return `選択肢: ${Object.values(field.options)
        .map((option) => option.label)
        .join(", ")}`;
    }
    return undefined;
  }, [value, kintoneFieldsMap]);

  return (
    <div className="mb-2">
      <label>{label}</label>
      <div className="control">
        <ReactSelect
          isClearable
          placeholder="フィールド名またはフィールドコードで検索"
          value={selected}
          onInputChange={onInputChange}
          isLoading={isLoading}
          loadingMessage={() => "フィールドを検索中..."}
          options={options}
          onChange={(v) => {
            const selected = v as SingleValue<ReactSelectOption<string>>;
            setSelected(selected ?? undefined);
            onChange(selected?.value ?? "");
          }}
          noOptionsMessage={({ inputValue }) =>
            inputValue.length === 0
              ? "フィールド名またはフィールドコードを入力してください"
              : "フィールドが見つかりません"
          }
          label=""
          description={description ?? ""}
          errorMessages={errorMessages}
          shouldDisplayError={shouldDisplayError}
        />
      </div>
      {helpText && <p className="help">{helpText}</p>}
    </div>
  );
};
